import { useState, useEffect, useId } from 'react';
import { KeyRound, Building2, UserCheck, ArrowRight, CheckCircle2 } from 'lucide-react';
import { TextField } from '@/components/TextField';
import { Button } from '@/components/Button';

interface OrgInfo {
  id: string;
  nombre: string;
  rut_rif?: string;
}

export function OnboardingPage() {
  const tokenId = useId();
  const nombreId = useId();
  const emailId = useId();
  const passwordId = useId();
  const pinId = useId();

  const [tokenInput, setTokenInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [step, setStep] = useState<'token' | 'user' | 'success'>('token');

  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setTokenInput(tokenParam.toUpperCase());
      verifyToken(tokenParam.toUpperCase());
    }
  }, []);

  const verifyToken = async (tokenToVerify: string) => {
    if (!tokenToVerify.trim()) return;
    setValidatingToken(true);
    setError(null);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/bootstrap-exchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(anonKey ? { apikey: anonKey } : {}),
        },
        body: JSON.stringify({ token: tokenToVerify.trim() }),
      });

      const data = await res.json();
      if (!res.ok || !data.valid) {
        throw new Error(data.error || 'Token inválido o expirado');
      }

      setOrg(data.organization || { id: data.organization_id, nombre: 'Mi Organización' });
      setStep('user');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error validando el token');
    } finally {
      setValidatingToken(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim() || !email.trim() || !password) {
      setError('Todos los campos son obligatorios');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/bootstrap-create-first-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(anonKey ? { apikey: anonKey } : {}),
        },
        body: JSON.stringify({
          token: tokenInput.trim(),
          email: email.trim(),
          password,
          nombre: nombre.trim(),
          pin: pin.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Error registrando el usuario inicial');
      }

      setStep('success');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al completar el registro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-4">
      <div className="bg-neutral-0 w-full max-w-md rounded-md border border-neutral-200 p-8 shadow-[var(--shadow-ambient-mid)]">
        {step === 'token' && (
          <div className="space-y-6">
            <div className="text-center">
              <div
                className="bg-primary-soft text-primary mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-md"
                aria-hidden="true"
              >
                <KeyRound className="h-6 w-6" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">Activar mi Tienda</h2>
              <p className="mt-1 text-sm text-neutral-500">
                Ingresa el código de 6 caracteres que te dio tu administrador.
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="border-danger bg-danger-soft text-danger rounded-md border p-3 text-center text-sm"
              >
                {error}
              </div>
            )}

            <div className="space-y-4">
              <TextField
                id={tokenId}
                label="Código Token"
                hideLabel
                required
                maxLength={6}
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="one-time-code"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value.toUpperCase())}
                placeholder="X7K2P9"
                helperText="6 caracteres alfanuméricos"
                leadingIcon={<KeyRound className="h-4 w-4" aria-hidden="true" />}
                className="text-center font-mono text-2xl tracking-widest uppercase"
              />

              <Button
                type="button"
                variant="primary"
                fullWidth
                onClick={() => verifyToken(tokenInput)}
                disabled={validatingToken || tokenInput.length < 6}
                loading={validatingToken}
                loadingLabel="Validando…"
                trailingIcon={<ArrowRight className="h-4 w-4" aria-hidden="true" />}
              >
                Continuar
              </Button>
            </div>
          </div>
        )}

        {step === 'user' && org && (
          <form onSubmit={handleRegister} className="space-y-5">
            <div className="text-center">
              <div
                className="bg-primary-soft text-primary mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-md"
                aria-hidden="true"
              >
                <Building2 className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-neutral-900">{org.nombre}</h2>
              <p className="mt-1 text-xs text-neutral-500">
                Configuración del usuario administrador inicial
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="border-danger bg-danger-soft text-danger rounded-md border p-3 text-center text-sm"
              >
                {error}
              </div>
            )}

            <div className="space-y-3 text-left">
              <TextField
                id={nombreId}
                label="Nombre completo"
                required
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Juan Pérez"
                autoComplete="name"
              />

              <TextField
                id={emailId}
                label="Correo electrónico"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="juan@mibodega.com"
                autoComplete="email"
                inputMode="email"
              />

              <TextField
                id={passwordId}
                label="Contraseña"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="Mínimo 6 caracteres"
                autoComplete="new-password"
              />

              <TextField
                id={pinId}
                label="PIN rápido POS (4 dígitos)"
                required
                maxLength={4}
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                helperText="Lo vas a usar para entrar sin internet"
                autoComplete="off"
                className="text-center font-mono tracking-widest"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              loadingLabel="Creando cuenta…"
              leadingIcon={<UserCheck className="h-4 w-4" aria-hidden="true" />}
            >
              Completar configuración
            </Button>
          </form>
        )}

        {step === 'success' && (
          <div className="space-y-4 py-4 text-center">
            <div
              className="rounded-pill bg-success-soft text-success mx-auto flex h-16 w-16 items-center justify-center"
              aria-hidden="true"
            >
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900">¡Configuración exitosa!</h2>
            <p className="text-sm text-neutral-600">
              Tu tienda ha sido activada y tu usuario administrador fue creado.
            </p>
            <Button
              type="button"
              variant="primary"
              fullWidth
              onClick={() => (window.location.href = '/')}
            >
              Ir a la Caja
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
